import { useEffect, useState } from "react";
import { api } from "../api/client";

/**
 * Métricas: tres preguntas, no diez tarjetas.
 *
 *   1. ¿El agente resuelve solo?
 *   2. ¿La cola está bajando?
 *   3. ¿Dónde se traba?
 *
 * Antes eran diez números planos, la mayoría en cero mientras nadie decidía —
 * medían el uso del portal, no el modelo, que es lo que se está validando.
 */

/** Estados del certificador, en palabras del analista. */
const ESTADO_ARCA: Record<string, string> = {
  CONFIRMADO: "Confirmado por el banco",
  PARCIAL: "Parcialmente confirmado",
  ESPERANDO_DATA: "ARCA no cargó aún",
  SIN_MOVIMIENTO_CON_COBERTURA: "Sin abono",
  BANCO_FUERA_DE_ARCA: "Banco fuera de ARCA",
  SIN_CODIGO: "Sin código de operación",
  SIN_FECHA: "Sin fecha",
  CONTRADICE: "Contradice el voucher",
  EXTORNADO: "Extornado",
  YA_CONSUMIDO: "Ya usado por otro depósito",
  SIN_CERTIFICAR: "Nunca pasó por el certificador",
};

/**
 * Variación contra el período anterior.
 *
 * `null` cuando no hay con qué comparar —el backend no pudo medirlo, o el
 * período anterior estaba vacío—. Un delta inventado sobre cero se lee como
 * "+100%" y es peor que no mostrar nada.
 */
function delta(actual: number, previo: number | null | undefined) {
  if (previo == null || !isFinite(previo) || previo === 0) return null;
  return (actual - previo) / previo;
}

/** El delta, ya escrito, con su color. Verde = mejor, y "mejor" depende de
 *  la métrica: menos tiempo hasta decidir es una mejora. */
function Delta({ v, invertido }: { v: number | null; invertido?: boolean }) {
  if (v == null || Math.abs(v) < 0.001) return null;
  const bueno = invertido ? v < 0 : v > 0;
  return (
    <span className="met-delta" style={{ color: `var(--${bueno ? "accent" : "danger"})` }}>
      {v > 0 ? "▲" : "▼"} {Math.abs(v * 100).toFixed(1)}%
    </span>
  );
}

/** Polilínea normalizada a la caja. Devuelve "" con menos de dos puntos:
 *  una línea de un solo punto no dibuja nada y ensucia el SVG. */
function linea(valores: number[], w: number, h: number, techo: number) {
  if (valores.length < 2) return "";
  const paso = w / (valores.length - 1);
  return valores
    .map((v, i) => `${(i * paso).toFixed(1)},${(h - (h * v) / techo).toFixed(1)}`)
    .join(" ");
}

export default function MetricsPage() {
  const [m, setM] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [dias, setDias] = useState(30);
  const [motivos, setMotivos] = useState<Record<string, string>>({});

  useEffect(() => {
    setM(null); setError(null);
    const hasta = Math.floor(Date.now() / 1000);
    api.getMetrics(hasta - dias * 24 * 3600, hasta)
      .then(setM).catch((e) => setError(e.message));
  }, [dias]);

  useEffect(() => {
    // Sin el catálogo la tabla mostraría los hashes de Admin en vez de
    // "El monto".
    api.getRejectReasons()
      .then((r) => setMotivos(Object.fromEntries(
        (r.items ?? []).map((x) => [x.id, x.label]))))
      .catch(() => setMotivos({}));
  }, []);

  if (error)
    return (
      <div className="empty falla">
        <div className="falla-titulo">No se pudieron cargar las métricas.</div>
        <div className="falla-detalle mono">{error}</div>
      </div>
    );
  if (!m) return <div className="empty">Cargando métricas...</div>;

  const pct = (v: number) => `${((v ?? 0) * 100).toFixed(1)}%`;
  const hayCola = m.cola != null;
  const porDia: any[] = m.por_dia ?? [];
  const maxDia = Math.max(1, ...porDia.map((d) => d.entraron));
  const resueltoSolo = m.cola_concluyente_ratio ?? 0;
  const prev = m.previo ?? null;
  const serie = porDia.slice(-21);

  // Qué hizo la persona con lo que el agente no pudo confirmar. Es la
  // pregunta de la validación del modelo, no un adorno: si casi todo el
  // amarillo termina aprobado, el agente está pidiendo criterio de más.
  const cri = m.criterio ?? { total: 0, aprobados: 0, rechazados: 0 };

  return (
    <>
      <div className="filters" style={{ marginBottom: 14 }}>
        {[7, 30, 90].map((d) => (
          <button key={d} className={"filter" + (dias === d ? " on" : "")}
                  onClick={() => setDias(d)}>
            {d} días
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: "var(--faint)", alignSelf: "center" }}>
          {m.total} depósitos en el período
          {m.previo != null && <> · comparado con los {m.previo.total} anteriores</>}
        </span>
        <button className="btn" disabled={!(m.por_dia ?? []).length}
                onClick={() => exportar(m, dias)}>
          Exportar CSV
        </button>
      </div>

      <div className="met-grid">
        {/* ── 1. ¿El agente resuelve solo? ──────────────────────────── */}
        <div className="card card-pad met-hero">
          <div className="rev-eyebrow">Lo que el agente resuelve sin ayuda</div>
          <div className="met-hero-num tabular">
            {hayCola ? pct(resueltoSolo) : "—"}
            <Delta v={delta(m.auto_ratio ?? 0, prev?.auto_ratio)} />
          </div>
          <div className="met-hero-sub">
            {hayCola
              ? <>{(m.cola.green ?? 0) + (m.cola.red ?? 0)} de {m.cola_total} en cola
                  son detecciones concluyentes — verde o rojo. El resto necesita
                  criterio humano.</>
              : "Disponible tras el próximo despliegue."}
          </div>

          {serie.length > 0 && (
            <>
              {/* Columnas apiladas por VEREDICTO: dicen cómo clasificó el
                  agente cada día, que es lo que se está validando. `sin dato`
                  son los registros anteriores a que el histórico guardara el
                  semáforo — van aparte en vez de repartirse entre los otros. */}
              <div className="met-barras" role="img"
                   aria-label="Cómo clasificó el agente, por día">
                {serie.map((d) => (
                  <div key={d.dia} className="met-col"
                       title={`${d.dia}: ${d.entraron} · ${d.green ?? 0} cuadran · ${d.yellow ?? 0} a criterio · ${d.red ?? 0} no coinciden`}>
                    <span className="met-col-total"
                          style={{ height: `${100 * d.entraron / maxDia}%` }}>
                      {(["red", "yellow", "green", "sin_dato"] as const).map((k) => (
                        (d[k] ?? 0) > 0 && (
                          <span key={k} className="met-tramo"
                                style={{
                                  height: `${100 * (d[k] ?? 0) / Math.max(1, d.entraron)}%`,
                                  background: k === "green" ? "var(--accent)"
                                    : k === "yellow" ? "var(--warn)"
                                    : k === "red" ? "var(--danger)" : "var(--raised)",
                                }} />
                        )
                      ))}
                    </span>
                  </div>
                ))}
              </div>
              <div className="met-leyenda">
                <span><i style={{ background: "var(--accent)" }} /> cuadran</span>
                <span><i style={{ background: "var(--warn)" }} /> a criterio</span>
                <span><i style={{ background: "var(--danger)" }} /> no coinciden</span>
                {serie.some((d) => (d.sin_dato ?? 0) > 0) && (
                  <span><i style={{ background: "var(--raised)" }} /> sin dato</span>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── 2. ¿La cola está bajando? ─────────────────────────────── */}
        <div className="card card-pad">
          <div className="rev-eyebrow">Cómo viene la cola</div>

          {/* Dos curvas: lo que entró y lo que se decidió. Un total no dice
              si la cola sube o baja; el cruce de las dos líneas sí, y es la
              pregunta que el analista tiene de verdad. */}
          {serie.length > 1 && (() => {
            const techo = Math.max(1, ...serie.map((d) => d.entraron));
            const W = 300, H = 76;
            return (
              <>
                <svg className="met-curvas" viewBox={`0 0 ${W} ${H}`}
                     preserveAspectRatio="none" role="img"
                     aria-label="Entraron contra decididos, por día">
                  <polyline fill="none" stroke="var(--muted)" strokeWidth="1.6"
                            vectorEffect="non-scaling-stroke"
                            points={linea(serie.map((d) => d.entraron), W, H, techo)} />
                  <polyline fill="none" stroke="var(--accent)" strokeWidth="1.8"
                            vectorEffect="non-scaling-stroke"
                            points={linea(serie.map((d) => d.decididos), W, H, techo)} />
                </svg>
                <div className="met-leyenda">
                  <span><i style={{ background: "var(--muted)" }} /> entraron</span>
                  <span><i style={{ background: "var(--accent)" }} /> decididos</span>
                </div>
              </>
            );
          })()}

          <div className="met-cola">
            <Dato label="Esperando decisión" valor={hayCola ? m.cola_total : "—"} />
            <Dato label="Ya decididos" valor={m.resueltos}
                  pie={`${m.approved} aprobados · ${m.rejected} rechazados`} />
          </div>
          {hayCola && (m.cola.green ?? 0) > 0 && (
            <div className="met-aviso">
              <b>{m.cola.green} depósitos ya cuadran</b> y están esperando que
              alguien los confirme. El agente no tiene nada más que aportar ahí.
            </div>
          )}
          {hayCola && (m.cola.yellow ?? 0) > 0 && (
            <div className="met-aviso met-aviso-warn">
              <b>{m.cola.yellow} necesitan criterio.</b> Son los que miden de
              verdad qué tan lejos está el modelo de resolver solo.
            </div>
          )}
        </div>
      </div>

      <div className="kpis" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))" }}>
        <Kpi label="Todo coincide" valor={hayCola ? m.cola.green : "—"}
             pie="listos para aprobar" tono="accent" />
        <Kpi label="Necesitan criterio" valor={hayCola ? m.cola.yellow : "—"}
             pie="el agente no pudo confirmarlo" tono="warn" />
        <Kpi label="No coinciden" valor={hayCola ? m.cola.red : "—"}
             pie="listos para rechazar" tono="danger" />
        <Kpi label="Certeza promedio" valor={pct(m.avg_confidence)}
             pie="qué tan seguro estuvo el agente"
             delta={delta(m.avg_confidence ?? 0, prev?.avg_confidence)} />
        <Kpi label="Tiempo hasta decidir"
             valor={m.avg_duration_ms ? `${(m.avg_duration_ms / 1000).toFixed(1)} s` : "—"}
             pie="desde que entra hasta que se decide"
             delta={delta(m.avg_duration_ms ?? 0, prev?.avg_duration_ms)} invertido />
        <Kpi label="Decididos por persona" valor={m.manual ?? 0}
             pie="el resto lo resolvió el agente"
             delta={delta(m.manual ?? 0, prev?.manual)} />
      </div>

      {/* ── 3. ¿Dónde se traba? ────────────────────────────────────── */}
      <div className="met-grid">
        <Panel titulo="Dónde se traba la cola"
               vacio="Sin datos de certificación en el período."
               filas={(m.arca_estados ?? []).map(([id, n]: [string, number]) => ({
                 label: ESTADO_ARCA[id] ?? id, n,
               }))} />
        <Panel titulo="Por qué se rechazaron"
               vacio="Sin rechazos en el período."
               filas={(m.top_reject_reasons ?? []).map(([id, n]: [string, number]) => ({
                 label: motivos[id] ?? id, n,
               }))} />
      </div>

      {/* ── Cierre ───────────────────────────────────────────────────
          La lectura, no el número. Si casi todo lo que el agente mandó a
          criterio termina aprobado, el agente está pidiendo criterio de más
          — y eso es trabajo humano que no hacía falta. */}
      {cri.total > 0 && (
        <div className="card card-pad">
          <div className="rev-seccion">
            Qué hace el analista con lo que el agente no pudo confirmar
          </div>
          <div className="rev-seccion-sub">
            {cri.total} depósitos en amarillo que ya se decidieron
          </div>
          <div className="met-cierre-barra" aria-hidden="true">
            <span style={{ width: `${100 * cri.aprobados / cri.total}%`,
                           background: "var(--accent)" }} />
            <span style={{ width: `${100 * cri.rechazados / cri.total}%`,
                           background: "var(--danger)" }} />
          </div>
          <div className="met-leyenda">
            <span><i style={{ background: "var(--accent)" }} />
              {Math.round(100 * cri.aprobados / cri.total)}% aprobados</span>
            <span><i style={{ background: "var(--danger)" }} />
              {Math.round(100 * cri.rechazados / cri.total)}% rechazados</span>
          </div>
          <div className="met-aviso">
            {cri.aprobados / cri.total >= 0.7
              ? <>El agente está siendo <b>más conservador de lo necesario</b>:
                  la mayoría de lo que manda a criterio termina aprobándose.</>
              : cri.rechazados / cri.total >= 0.7
                ? <>El amarillo está atrapando problemas reales: casi todo lo
                    que manda a criterio termina rechazándose.</>
                : <>El amarillo está bien puesto: se reparte entre aprobados y
                    rechazados, así que hay criterio real que aportar.</>}
          </div>
        </div>
      )}

      {(m.por_operador ?? []).length > 0 && (
        <Panel titulo="Quién decidió"
               vacio=""
               filas={m.por_operador.map(([quien, n]: [string, number]) => ({
                 label: quien === "auto" ? "El agente" : quien, n,
               }))} />
      )}
    </>
  );
}

/** El CSV lleva la serie por día, que es lo que alguien va a querer cruzar
 *  con otra planilla. Los agregados se recalculan solos desde ahí. */
function exportar(m: any, dias: number) {
  const SALTO = String.fromCharCode(10);
  const cab = "dia,entraron,decididos,aprobados,rechazados";
  const filas = (m.por_dia ?? []).map((d: any) =>
    [d.dia, d.entraron, d.decididos, d.aprobados, d.rechazados].join(","));
  const url = URL.createObjectURL(
    new Blob([[cab, ...filas].join(SALTO)], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `metricas-${dias}d-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function Dato({ label, valor, pie }: { label: string; valor: any; pie?: string }) {
  return (
    <div>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value tabular">{valor ?? "—"}</div>
      {pie && <div className="kpi-foot">{pie}</div>}
    </div>
  );
}

function Kpi({ label, valor, pie, tono, delta: d, invertido }: {
  label: string; valor: any; pie?: string;
  tono?: "accent" | "warn" | "danger";
  delta?: number | null; invertido?: boolean;
}) {
  return (
    <div className="card kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value tabular"
           style={tono ? { color: `var(--${tono})` } : undefined}>
        {valor ?? "—"}
        {d != null && <Delta v={d} invertido={invertido} />}
      </div>
      {pie && <div className="kpi-foot">{pie}</div>}
    </div>
  );
}

/** Barras horizontales con etiqueta, conteo y porcentaje. */
function Panel({ titulo, filas, vacio }: {
  titulo: string; vacio: string; filas: { label: string; n: number }[];
}) {
  const total = filas.reduce((s, f) => s + f.n, 0);
  return (
    <div className="card card-pad">
      <div className="rev-seccion">{titulo}</div>
      {filas.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 10 }}>{vacio}</div>
      ) : (
        <div className="met-filas">
          {filas.map((f) => (
            <div key={f.label} className="met-fila">
              <div className="met-fila-label">{f.label}</div>
              <div className="tabular met-fila-n">{f.n}</div>
              <div className="tabular met-fila-pct">
                {total ? `${Math.round(100 * f.n / total)}%` : "—"}
              </div>
              <div className="met-fila-barra">
                <span style={{ width: `${total ? 100 * f.n / total : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
